import { AlertCircle, AlertTriangle, ArrowRight, ArrowUpRight, CalendarRange, RefreshCcw, ShieldAlert, TrendingUp } from "lucide-react";
import { useMemo, useState } from "react";
import { Skeleton } from "../../../components/skeleton";
import { useDealForecast } from "../../../hooks/use-crm-forecast";
import { useRiskRadar } from "../../../hooks/use-crm-risk-radar";
import type { DealRiskAssessment } from "../../../hooks/use-crm-risk-radar";
import { formatCurrency, formatMonthYear } from "../../../lib/i18n";

const RISK_LEVEL_CONFIG = {
  critical: { color: "text-[var(--color-destructive)]", bg: "bg-[var(--color-destructive)]/10", label: "Critical" },
  high: { color: "text-[var(--color-warning)]", bg: "bg-[var(--color-warning)]/10", label: "High" },
  medium: { color: "text-[var(--color-muted-foreground)]", bg: "bg-[var(--color-muted)]/50", label: "Medium" },
  low: { color: "text-[var(--color-success)]", bg: "bg-[var(--color-success)]/10", label: "Low" },
} as const;

function getCurrentMonthValue() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

function formatPeriodLabel(period: string) {
  const [year, month] = period.split("-");
  if (!year || !month) return period;
  return formatMonthYear(new Date(Date.UTC(Number(year), Number(month) - 1, 1)));
}

export default function RevenueForecastPage() {
  const [period, setPeriod] = useState(getCurrentMonthValue);
  const currentPeriod = useMemo(() => getCurrentMonthValue(), []);
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useDealForecast(period);
  const {
    data: riskData,
    isLoading: riskLoading,
    error: riskError,
    refetch: refetchRisk,
  } = useRiskRadar();

  const committed = data?.lanes.find((lane) => lane.label === "committed")?.totalCents ?? 0;
  const bestCase = data?.lanes.find((lane) => lane.label === "best_case")?.totalCents ?? 0;
  const pipeline = data?.lanes.find((lane) => lane.label === "pipeline")?.totalCents ?? 0;
  const pctOfQuota = data && data.teamQuotaCents > 0
    ? Math.round((data.teamAchievedCents / data.teamQuotaCents) * 100)
    : 0;
  const forecastGapCents = Math.max((data?.teamQuotaCents ?? 0) - (data?.projectedTeamCents ?? 0), 0);
  const isCurrentPeriod = period === currentPeriod;
  const pipelineCoveragePct = data && data.teamQuotaCents > 0
    ? Math.round(((data.projectedTeamCents ?? 0) / data.teamQuotaCents) * 100)
    : 0;
  const repQuotas = data?.repQuotas ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Revenue Forecast</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Mounted quota, AI forecast, and risk pressure for {formatPeriodLabel(period)}.
          </p>
        </div>
        <div className="flex flex-col gap-3 rounded-xl border bg-[var(--color-card)] p-4 sm:flex-row sm:items-end">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-foreground)]">Forecast period</span>
            <div className="relative">
              <CalendarRange className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
              <input
                aria-label="Forecast period"
                type="month"
                value={period}
                max={currentPeriod}
                onChange={(event) => setPeriod(event.target.value)}
                className="h-10 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] pl-9 pr-3 text-sm text-[var(--color-foreground)]"
              />
            </div>
          </label>
          <button
            type="button"
            onClick={() => setPeriod(currentPeriod)}
            disabled={isCurrentPeriod}
            className="h-10 rounded-lg border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            This month
          </button>
          <button
            type="button"
            onClick={() => {
              refetch();
              refetchRisk();
            }}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-3 text-sm font-medium text-[var(--color-primary-foreground)]"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>
      {isLoading ? <Skeleton className="h-64" /> : error ? (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="mr-2 inline h-4 w-4" />
          {error}
          <button type="button" onClick={refetch} className="ml-3 underline">
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="grid gap-4 xl:grid-cols-[1.3fr_0.7fr]">
            <div className="border rounded-xl p-6 bg-[var(--color-card)]">
              <div className="flex justify-between mb-2">
                <span className="font-medium">Quota Progress</span>
                <span className="font-bold">
                  {pctOfQuota}% ({formatCurrency(data?.teamAchievedCents ?? 0)} / {formatCurrency(data?.teamQuotaCents ?? 0)})
                </span>
              </div>
              <div className="h-4 bg-[var(--color-border)] rounded-full overflow-hidden">
                <div className="h-full bg-[var(--color-primary)] rounded-full transition-all" style={{ width: `${Math.min(100, pctOfQuota)}%` }} />
              </div>
              <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
                Projected team finish: {formatCurrency(data?.projectedTeamCents ?? 0)}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
              <div className="rounded-xl border bg-[var(--color-card)] p-4">
                <div className="text-sm text-[var(--color-muted-foreground)]">Quota coverage</div>
                <div className="mt-1 text-2xl font-bold text-[var(--color-foreground)]">{pipelineCoveragePct}%</div>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  Projected finish against the selected period quota.
                </p>
              </div>
              <div className="rounded-xl border bg-[var(--color-card)] p-4">
                <div className="text-sm text-[var(--color-muted-foreground)]">Remaining gap</div>
                <div className="mt-1 text-2xl font-bold text-[var(--color-warning)]">{formatCurrency(forecastGapCents)}</div>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {forecastGapCents > 0 ? "Revenue still needed to hit quota." : "Projected finish is on or above target."}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: "Committed", value: committed, color: "var(--color-success)", count: data?.lanes.find((lane) => lane.label === "committed")?.dealCount ?? 0 },
              { label: "Best Case", value: bestCase, color: "var(--color-primary)", count: data?.lanes.find((lane) => lane.label === "best_case")?.dealCount ?? 0 },
              { label: "Pipeline", value: pipeline, color: "var(--color-muted-foreground)", count: data?.lanes.find((lane) => lane.label === "pipeline")?.dealCount ?? 0 },
              { label: "AI Prediction", value: data?.aiPrediction.predictedCents ?? 0, color: "var(--color-ai)", count: null },
            ].map((tier) => (
              <div key={tier.label} className="border rounded-lg p-4 bg-[var(--color-card)]">
                <div className="text-sm text-[var(--color-muted-foreground)]">{tier.label}</div>
                <div className="text-2xl font-bold" style={{ color: tier.color }}>{formatCurrency(tier.value)}</div>
                {tier.count !== null ? (
                  <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{tier.count} active deal{tier.count === 1 ? "" : "s"}</div>
                ) : (
                  <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">Historical pattern model</div>
                )}
              </div>
            ))}
          </div>

          <div className="p-4 rounded-lg bg-[var(--color-ai-light,var(--color-secondary))]/5 border border-[var(--color-ai)]/20">
            <div className="flex justify-between mb-2">
              <div className="flex items-center gap-2 font-medium text-[var(--color-ai)]">
                <TrendingUp className="h-4 w-4" />
                AI Prediction: {formatCurrency(data?.aiPrediction.predictedCents ?? 0)}
              </div>
              <span className="rounded-full bg-[var(--color-ai)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-ai)]">
                {data?.aiPrediction.confidencePercent ?? 0}% confidence
              </span>
            </div>
            <p className="mt-1 text-sm">
              Confidence band: {formatCurrency(data?.aiPrediction.confidenceLowCents ?? 0)} <ArrowRight className="mx-1 inline h-3.5 w-3.5" /> {formatCurrency(data?.aiPrediction.confidenceHighCents ?? 0)}
            </p>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{data?.aiPrediction.reasoning}</p>
          </div>

          <div className="border rounded-xl bg-[var(--color-card)]">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h2 className="font-semibold text-[var(--color-foreground)]">Rep quota tracking</h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">{data?.aiSummary}</p>
              </div>
            </div>
            <div className="divide-y">
              {repQuotas.length > 0 ? repQuotas.map((rep) => (
                <div key={rep.repId} className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="font-medium text-[var(--color-foreground)]">{rep.repName}</div>
                    <div className="text-sm text-[var(--color-muted-foreground)]">{rep.guidance}</div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <div className="text-[var(--color-muted-foreground)]">Actual</div>
                      <div className="font-medium">{formatCurrency(rep.achievedCents)}</div>
                    </div>
                    <div>
                      <div className="text-[var(--color-muted-foreground)]">Forecast</div>
                      <div className="font-medium">{formatCurrency(rep.forecastCents)}</div>
                    </div>
                    <div>
                      <div className="text-[var(--color-muted-foreground)]">Gap</div>
                      <div className="font-medium">{formatCurrency(Math.max(rep.quotaCents - rep.forecastCents, 0))}</div>
                    </div>
                    <div className={rep.isOnTrack ? "text-[var(--color-success)]" : "text-[var(--color-warning)]"}>
                      <div className="text-[var(--color-muted-foreground)]">Quota</div>
                      <div className="flex items-center gap-1 font-medium">
                        {rep.pctOfQuota}%
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="px-4 py-6 text-sm text-[var(--color-muted-foreground)]">
                  No rep quotas are configured for {formatPeriodLabel(period)} yet.
                </div>
              )}
            </div>
          </div>

          {/* Risk Summary */}
          <RiskSummarySection deals={riskData ?? []} isLoading={riskLoading} error={riskError} onRetry={refetchRisk} />
        </>
      )}
    </div>
  );
}

function RiskSummarySection({
  deals,
  isLoading,
  error,
  onRetry,
}: {
  deals: DealRiskAssessment[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  if (isLoading) return <Skeleton className="h-48 mt-6" />;
  if (error) {
    return (
      <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="mr-2 inline h-4 w-4" />
        {error}
        <button type="button" onClick={onRetry} className="ml-3 underline">
          Retry
        </button>
      </div>
    );
  }
  if (deals.length === 0) return null;

  const atRiskDeals = deals.filter((d) => d.riskLevel === "critical" || d.riskLevel === "high");
  const atRiskRevenueCents = atRiskDeals.reduce((sum, d) => sum + d.valueCents, 0);
  const stalledDeals = deals.filter((d) => d.factors.some((f) => f.factor === "activity_stall"));
  const criticalCount = deals.filter((d) => d.riskLevel === "critical").length;
  const highCount = deals.filter((d) => d.riskLevel === "high").length;

  return (
    <div className="mt-6 space-y-4">
      {/* Risk summary cards */}
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-[var(--color-destructive)]" />
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Pipeline Risk</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border rounded-lg p-4 bg-[var(--color-card)]">
          <div className="text-sm text-[var(--color-muted-foreground)]">At-Risk Revenue</div>
          <div className="text-2xl font-bold text-[var(--color-destructive)]">
            {formatCurrency(atRiskRevenueCents)}
          </div>
          <div className="text-xs text-[var(--color-muted-foreground)] mt-1">
            {atRiskDeals.length} deal{atRiskDeals.length !== 1 ? "s" : ""} at risk
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-[var(--color-card)]">
          <div className="text-sm text-[var(--color-muted-foreground)]">Stalled Deals</div>
          <div className="text-2xl font-bold text-[var(--color-warning)]">{stalledDeals.length}</div>
          <div className="text-xs text-[var(--color-muted-foreground)] mt-1">
            No activity beyond threshold
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-[var(--color-card)]">
          <div className="text-sm text-[var(--color-muted-foreground)]">Risk Breakdown</div>
          <div className="flex items-center gap-3 mt-1">
            {criticalCount > 0 && (
              <span className="text-sm font-medium text-[var(--color-destructive)]">
                {criticalCount} critical
              </span>
            )}
            {highCount > 0 && (
              <span className="text-sm font-medium text-[var(--color-warning)]">
                {highCount} high
              </span>
            )}
          </div>
        </div>
      </div>

      {/* At-risk deals list */}
      {atRiskDeals.length > 0 && (
        <div className="border rounded-xl bg-[var(--color-card)]">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold text-[var(--color-foreground)]">Deals Needing Attention</h3>
          </div>
          <div className="divide-y">
            {atRiskDeals.slice(0, 10).map((deal) => {
              const config = RISK_LEVEL_CONFIG[deal.riskLevel];
              const topRec = deal.recommendations[0];
              return (
                <div key={deal.dealId} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${config.color}`} />
                      <span className="font-medium text-[var(--color-foreground)]">{deal.dealTitle}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                        {config.label}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCurrency(deal.valueCents)}
                    </span>
                  </div>
                  {deal.factors[0] && (
                    <p className="text-xs text-[var(--color-muted-foreground)] mt-1 ml-6">
                      {deal.factors[0].description}
                    </p>
                  )}
                  {topRec && (
                    <p className="text-xs text-[var(--color-foreground)] mt-1 ml-6 font-medium">
                      {topRec.urgency === "immediate" ? "Now" : topRec.urgency === "this_week" ? "This week" : "Next week"}: {topRec.action}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
